import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { 
  Bell, 
  FlaskConical, 
  Scan, 
  ClipboardCheck, 
  Calendar, 
  AlertTriangle,
  Package,
  Wrench,
  MessageSquare,
  UserCheck,
  X
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import type { Notification } from "@/hooks/useNotifications";

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
  onClose?: () => void;
}

const NOTIFICATION_ICONS: Record<string, React.ElementType> = {
  lab_case_ready: FlaskConical,
  lab_case_delayed: AlertTriangle,
  radiology_report_ready: Scan,
  task_assigned: ClipboardCheck,
  task_overdue: AlertTriangle,
  appointment_checkin: Calendar,
  clinical_visit_completed: UserCheck,
  inventory_low_stock: Package,
  equipment_down: Wrench,
  vendor_comment_received: MessageSquare,
  default: Bell,
};

const PRIORITY_STYLES: Record<string, string> = {
  low: "border-l-muted-foreground/30",
  normal: "border-l-primary/50",
  high: "border-l-amber-500",
  urgent: "border-l-destructive",
};

export function NotificationItem({
  notification,
  onMarkAsRead,
  onDelete,
  onClose,
}: NotificationItemProps) {
  const navigate = useNavigate();
  const Icon = NOTIFICATION_ICONS[notification.type] || NOTIFICATION_ICONS.default;

  const handleClick = () => {
    if (!notification.is_read) {
      onMarkAsRead(notification.id);
    }
    if (notification.action_url) {
      navigate(notification.action_url);
      onClose?.();
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(notification.id);
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        "group flex items-start gap-3 p-3 border-l-4 cursor-pointer transition-colors",
        "hover:bg-muted/50",
        PRIORITY_STYLES[notification.priority],
        !notification.is_read && "bg-primary/5"
      )}
    >
      {/* Icon */}
      <div
        className={cn(
          "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
          notification.priority === "urgent"
            ? "bg-destructive/10 text-destructive"
            : notification.priority === "high"
            ? "bg-amber-500/10 text-amber-500"
            : "bg-primary/10 text-primary"
        )}
      >
        <Icon className="h-4 w-4" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p
            className={cn(
              "text-sm font-medium line-clamp-1",
              !notification.is_read && "text-foreground",
              notification.is_read && "text-muted-foreground"
            )}
          >
            {notification.title}
          </p>
          {!notification.is_read && (
            <span className="flex-shrink-0 w-2 h-2 rounded-full bg-primary mt-1.5" />
          )}
        </div>
        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
          {notification.message}
        </p>
        <p className="text-xs text-muted-foreground/70 mt-1">
          {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
        </p>
      </div>

      {/* Delete button */}
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={handleDelete}
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  );
}
